/**
 * 分支合并 API 服务
 *
 * 封装与分支合并相关的所有后端 API 调用
 */
import wujiFetch from '@/utils/wujiFetch';
import { message } from '@utils/wuji-message';
import { $t } from '@/i18n';
import getXyManagePathPrefix from '@/utils/getXyManagePathPrefix';
import { BRANCH_API_BASE_URL, BRANCH_API_TIMEOUT, BRANCH_MERGE_SUBMIT_TIMEOUT, } from '../constants';
/**
 * 分支合并服务类
 *
 * @description
 * 提供分支合并相关的 API 操作：
 * - 获取分支冲突数据
 * - 提交合并结果
 * - 获取分支信息
 *
 * @example
 * ```typescript
 * const service = new MergeService();
 *
 * // 提交合并
 * await service.submitMerge({
 *   projectId: 'proj123',
 *   branchId: 'feature-1',
 *   mergeBranchId: 'master',
 *   data: [...],
 *   desc: '合并 master 到 feature-1',
 * });
 * ```
 */
export class MergeService {
    /** 读取目标配置，不使用当前路由分支，也不更新编辑器/Vuex 状态。 */
    async getTargetAIProjectInfo(projectId, branchId) {
        const prefix = getXyManagePathPrefix({
            envId: 'dev',
            branchId: branchId === 'master' ? null : branchId,
        });
        const query = new URLSearchParams({
            projectId,
            // 产物由容器替换；保留 editorConfig、collectionRefs 等完整目标配置。
            exclude: 'artifact,artifactCos,ctime,mtime,_ctime,_mtime',
        });
        const info = await wujiFetch(`${prefix}/ai-project/info?${query.toString()}`, {
            method: 'GET',
            timeout: BRANCH_API_TIMEOUT,
        });
        if (!info || typeof info !== 'object' || Array.isArray(info)
            || typeof info.mainBranch !== 'string' || !/^[0-9a-f]{40}$/i.test(info.mainBranch)
            || (info.projectId && info.projectId !== projectId)) {
            throw new Error($t('k_d4fd7411'));
        }
        return info;
    }
    /**
     * 提交分支合并
     *
     * @description
     * 将解决完冲突的数据提交到后端，完成分支合并
     *
     * @param params - 提交参数
     * @returns 后端返回的合并结果
     */
    async submitMerge(params) {
        const { projectId, branchId, mergeBranchId, data, desc, source } = params;
        try {
            const result = await wujiFetch(`${BRANCH_API_BASE_URL}/${branchId}/merge/${mergeBranchId}?projectid=${projectId}`, {
                method: 'POST',
                body: JSON.stringify({
                    data,
                    desc,
                    ...(source ? { source } : {}),
                }),
                // 合并提交会在服务端触发 COS 上传（artifact resolve + upload），耗时可能超过 1 分钟
                timeout: BRANCH_MERGE_SUBMIT_TIMEOUT,
            });
            if (!result
                || typeof result !== 'object'
                || ![result.id, result._id]
                    .some(id => typeof id === 'string' && id.length > 0)) {
                throw new Error($t('k_1a378776'));
            }
            return result;
        }
        catch (err) {
            message.error($t('k_b98a8a6', { arg_0: err.message }));
            console.error('[MergeService] Submit merge failed:', err);
            throw err;
        }
    }
    /**
     * 获取分支冲突数据
     *
     * @description
     * 从后端获取两个分支之间的冲突数据
     *
     * @param projectId - 项目 ID
     * @param branchId - 当前分支 ID
     * @param mergeBranchId - 合并来源分支 ID
     * @returns 冲突数据
     */
    async getConflictData(projectId, branchId, mergeBranchId) {
        try {
            const res = await wujiFetch(`${BRANCH_API_BASE_URL}/${branchId}/conflict/${mergeBranchId}?projectid=${projectId}`, {
                method: 'GET',
                timeout: BRANCH_API_TIMEOUT,
            });
            return res;
        }
        catch (err) {
            console.error('[MergeService] Get conflict data failed:', err);
            throw err;
        }
    }
    /**
     * 在合并成功后刷新页面
     *
     * @description
     * 非 master 分支合并成功后需要刷新页面以加载最新数据
     *
     * @param branchId - 当前分支 ID
     * @param delay - 刷新延迟（毫秒）
     */
    refreshAfterMerge(branchId, delay = 500) {
        if (branchId !== 'master') {
            setTimeout(() => window.location.reload(), delay);
        }
    }
}
/**
 * 默认服务实例
 */
export const mergeService = new MergeService();
export default mergeService;
